import { useState } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Logo,
  PasswordField,
  PhoneField,
  PhoneFieldResult,
  Spacing,
  StyledDiv,
  Typography,
  TextField,
  Select,
  SelectOption,
  AuthContainer,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export enum SignupGender {
  Male = 'male',
  Female = 'female',
}

export enum SignupRole {
  Teacher = 'teacher',
  Principal = 'principal',
  Administrator = 'administrator',
}

export enum OtpDeliveryMethod {
  SMS = 'SMS',
  WhatsApp = 'WHATSAPP',
}

interface SignUpFormProps {
  logo: string;
  phoneValue: string;
  passwordValue: string;
  confirmPasswordValue: string;
  nameValue: string;
  genderValue: SignupGender | '';
  roleValue: SignupRole | '';
  schoolNameValue: string;
  otpDeliveryMethod: OtpDeliveryMethod;
  onPhoneChange: (data: PhoneFieldResult) => void;
  onPasswordChange: (data: PhoneFieldResult) => void;
  onConfirmPasswordChange: (data: PhoneFieldResult) => void;
  onNameChange: (value: string) => void;
  onGenderChange: (value: SignupGender) => void;
  onRoleChange: (value: SignupRole) => void;
  onSchoolNameChange: (value: string) => void;
  onOtpDeliveryMethodChange: (value: OtpDeliveryMethod) => void;
  onSubmit: () => void;
  onBack: () => void;
  phoneError?: string;
  passwordError?: string;
  confirmPasswordError?: string;
  nameError?: string;
  genderError?: string;
  roleError?: string;
  schoolNameError?: string;
  isLoading?: boolean;
}

export const SignUpForm = ({
  logo,
  phoneValue,
  passwordValue,
  confirmPasswordValue,
  nameValue,
  genderValue,
  roleValue,
  schoolNameValue,
  otpDeliveryMethod,
  onPhoneChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onNameChange,
  onGenderChange,
  onRoleChange,
  onSchoolNameChange,
  onOtpDeliveryMethodChange,
  onSubmit,
  onBack,
  phoneError,
  passwordError,
  confirmPasswordError,
  nameError,
  genderError,
  roleError,
  schoolNameError,
  isLoading = false,
}: SignUpFormProps) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    useState<boolean>(false);

  const genderOptions: SelectOption[] = [
    { value: '', content: 'Select Gender' },
    { value: SignupGender.Male, content: 'Male' },
    { value: SignupGender.Female, content: 'Female' },
  ];

  const roleOptions: SelectOption[] = [
    { value: '', content: 'Select Role' },
    { value: SignupRole.Teacher, content: 'Teacher' },
    { value: SignupRole.Principal, content: 'Principal' },
    { value: SignupRole.Administrator, content: 'Administrator' },
  ];

  const otpDeliveryOptions: SelectOption[] = [
    { value: OtpDeliveryMethod.SMS, content: 'SMS' },
    { value: OtpDeliveryMethod.WhatsApp, content: 'WhatsApp' },
  ];

  const shouldShowSchoolName =
    roleValue === SignupRole.Teacher || roleValue === SignupRole.Principal;
  return (
    <ScrollableAuthContainer>
      <StyledImageWrapper>
        <Logo
          altName="signup-form-logo"
          width={275}
          src={`../../assets/${logo}`}
          data-cy="signup-form-logo"
        />
      </StyledImageWrapper>

      <StyledDiv flexDirection="column" gap={4}>
        <Spacing marginBottom="sm">
          <Typography
            variant="textHeadlineLargeSurface"
            data-cy="signup-form-title"
          >
            Create Account
          </Typography>
        </Spacing>

        <Spacing marginBottom="xl">
          <Typography
            variant="textBodyLargeSurface"
            data-cy="signup-form-description"
          >
            Enter your details to create your account
          </Typography>
        </Spacing>

        <Spacing marginBottom="lg">
          <PhoneField
            name="phone"
            type="tel"
            variant="outlined"
            label="Phone Number"
            placeholder="Enter your phone number"
            value={phoneValue}
            autocomplete="tel"
            data-cy="signup-phone-field"
            errorText={phoneError}
            onChange={onPhoneChange}
            international={true}
            defaultCountry="pk"
          />
        </Spacing>

        <Spacing marginBottom="lg">
          <PasswordField
            name="password"
            type={passwordVisible ? 'text' : 'password'}
            variant="outlined"
            label="Password"
            placeholder="Create password"
            value={passwordValue}
            autocomplete="new-password"
            data-cy="signup-password-field"
            errorText={passwordError}
            onChange={onPasswordChange}
            onToggleVisibility={() => setPasswordVisible((prev) => !prev)}
          />
        </Spacing>

        <Spacing marginBottom="lg">
          <PasswordField
            name="confirmPassword"
            type={confirmPasswordVisible ? 'text' : 'password'}
            variant="outlined"
            label="Confirm Password"
            placeholder="Confirm your password"
            value={confirmPasswordValue}
            autocomplete="new-password"
            data-cy="signup-confirm-password-field"
            errorText={confirmPasswordError}
            onChange={onConfirmPasswordChange}
            onToggleVisibility={() =>
              setConfirmPasswordVisible((prev) => !prev)
            }
          />
        </Spacing>

        <Spacing marginBottom="lg">
          <TextField
            name="fullName"
            type="text"
            variant="outlined"
            label="Full Name"
            placeholder="Enter full name"
            value={nameValue}
            autocomplete="name"
            data-cy="signup-name-field"
            errorText={nameError}
            error={!!nameError}
            onInput={(e) => onNameChange(e.target.value)}
            onKeyPress={(e) => {
              // Only allow alphabets and spaces
              if (!/[a-zA-Z\s]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
        </Spacing>

        <Spacing marginBottom="lg">
          <StyledDiv flexDirection="column">
            <Select
              key={`gender-${genderValue}`}
              variant="outlined"
              label="Gender"
              options={genderOptions}
              defaultValue={genderValue || ''}
              onChange={(option) =>
                onGenderChange(option.value as SignupGender)
              }
            />
            {genderError && (
              <StyledErrorWrapper>
                <Typography
                  variant="textBodySmallSchemesError"
                  data-cy="signup-gender-error"
                >
                  {genderError}
                </Typography>
              </StyledErrorWrapper>
            )}
          </StyledDiv>
        </Spacing>

        <Spacing marginBottom="sm">
          <StyledDiv flexDirection="column">
            <Select
              key={`role-${roleValue}`}
              variant="outlined"
              label="Role"
              options={roleOptions}
              defaultValue={roleValue || ''}
              onChange={(option) => onRoleChange(option.value as SignupRole)}
            />
            {roleError && (
              <StyledErrorWrapper>
                <Typography
                  variant="textBodySmallSchemesError"
                  data-cy="signup-role-error"
                >
                  {roleError}
                </Typography>
              </StyledErrorWrapper>
            )}
          </StyledDiv>
        </Spacing>

        {shouldShowSchoolName && (
          <Spacing marginBottom="lg">
            <TextField
              name="schoolName"
              type="text"
              variant="outlined"
              label="School Name"
              placeholder="Enter school name"
              value={schoolNameValue}
              autocomplete="organization"
              data-cy="signup-school-name-field"
              errorText={schoolNameError}
              error={!!schoolNameError}
              onInput={(e) => onSchoolNameChange(e.target.value)}
            />
          </Spacing>
        )}

        <Spacing marginBottom="sm" />
        <StyledDiv flexDirection="column">
          <Select
            key={`otp-${otpDeliveryMethod}`}
            variant="outlined"
            label="Receive OTP via"
            options={otpDeliveryOptions}
            defaultValue={otpDeliveryMethod}
            onChange={(option) =>
              onOtpDeliveryMethodChange(option.value as OtpDeliveryMethod)
            }
          />
        </StyledDiv>
      </StyledDiv>

      <Spacing marginTop="lg" />

      <StyledDiv flexDirection="column" gap={8}>
        <Button
          children="Create Account"
          onClick={onSubmit}
          loading={isLoading}
          data-cy="create-account-button"
        />

        <Button
          children="Back"
          onClick={onBack}
          variant="text"
          data-cy="back-button"
        />
      </StyledDiv>
    </ScrollableAuthContainer>
  );
};

const StyledImageWrapper = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--rk-spacing-sm);
  ${mq(BreakpointSizes.xs)} {
    display: flex;
  }
`;

const StyledErrorWrapper = styled.div`
  margin-top: var(--rk-spacing-sm);
`;

const ScrollableAuthContainer = styled(AuthContainer)`
  max-height: 90vh;
  overflow-y: auto;
  ${mq(BreakpointSizes.xs)} {
    max-height: none;
    overflow-y: visible;
  }
`;
